import { useCallback, useEffect, useState } from 'react';

import { type AlertDialogProps } from '@backyard-ui/core';

import { useRegionByZipCode } from '@/presenters/hooks/catalog/product/use-region-by-zip-code';
import { useGlobalStore } from '@/presenters/store/global';
import { ZIP_CODE } from '@/utils/constants/cookies';
import { cookies } from '@/utils/cookies';
import { userContext } from '@/utils/user-context';

interface UseRegionCheckerProps {
  zipCode: string;
  zipCodeIsValid: boolean;
}

type Status = 'idle' | 'valid' | 'invalid';

export default function useRegionChecker(props: UseRegionCheckerProps) {
  const { zipCode, zipCodeIsValid } = props;
  const [recontextAlertDialogIsOpen, setRecontextAlertDialogIsOpen] =
    useState(false);

  const currentRegion = useGlobalStore((state) => state.session?.region);

  const onOpenChangeRecontextAlertDialog: AlertDialogProps['onOpenChange'] = (
    status
  ) => setRecontextAlertDialogIsOpen(status);

  const { data, isFetching, isError, isFetched } = useRegionByZipCode(
    { zipCode },
    { enabled: zipCodeIsValid }
  );

  const recontext = useCallback(async () => {
    if (data?.data.id) {
      await userContext.recontext({
        contextBy: 'zip_code',
        zipCodeRangeId: data.data.id,
        zipCode,
      });

      userContext.clearRegion();
    }
  }, [data?.data.id, zipCode]);

  const getStatus = useCallback<() => Status>(() => {
    if (currentRegion && data?.data.region) {
      if (currentRegion === data?.data.region) {
        return 'valid';
      }

      return 'invalid';
    }

    return 'idle';
  }, [currentRegion, data?.data.region]);

  const status = getStatus();

  useEffect(() => {
    if (status === 'invalid') {
      setRecontextAlertDialogIsOpen(true);
    } else if (status === 'valid') {
      const zipCodeCookieOptions = cookies.createOptions({
        name: ZIP_CODE,
        value: zipCode,
      });

      cookies.client.set(zipCodeCookieOptions);
    }
  }, [status, zipCode]);

  return {
    data,
    isFetching,
    status,
    onOpenChangeRecontextAlertDialog,
    recontextAlertDialogIsOpen,
    recontext,
    isError,
    isFetched,
  };
}
